import type { DocumentResult, LineItemRow } from '../shared/desktop-api';
import type {
  FieldDraft,
  FieldDraftBinding,
  FieldDraftEvent,
  FieldDraftOutcome,
} from './field-drafts';
import { documentFieldContractToken } from './save-lane';
import { reviewDocumentMutationToken } from './review-workspace';

// Line cells use the already shared, bounded draft state machine, in a separate
// store/message scope. `fieldId` is its key, not a job-schema field identity.
export const lineDraftCells = ['description', 'quantity', 'unitPrice', 'amount'] as const;
export type LineDraftCell = (typeof lineDraftCells)[number];
export const lineDraftTarget = (draft: Pick<FieldDraft, 'fieldId'>) => {
  const match = /^line:([1-9][0-9]*):(description|quantity|unitPrice|amount)$/.exec(draft.fieldId);
  return match ? { rowNumber: Number(match[1]), cell: match[2] as LineDraftCell } : null;
};
export const lineDraftText = (row: LineItemRow, cell: LineDraftCell) => String(row[cell] ?? '');
export const readLineDraftValue = (
  cell: LineDraftCell,
  raw: string,
): string | number | null | undefined => {
  if (cell === 'description') return raw;
  const cleaned = raw.replace(/,/g, '').trim();
  if (!cleaned) return null;
  const number = Number(cleaned);
  return Number.isFinite(number) ? number : undefined;
};
const rowEvidence = (row: LineItemRow) =>
  JSON.stringify([row.pageNumber, row.evidenceLineIndexes ?? [], row.cellEvidence ?? null]);
/** No row IDs exist in storage. Never mistake an index after a structural edit
 * for the old row: source/type/schema and the entire ordered table must match.
 * Ungrounded or duplicate-evidence rows cannot be rebound after any revision. */
export const lineDraftTableIdentity = (document: DocumentResult) =>
  JSON.stringify([
    documentFieldContractToken(document),
    document.pages.map((page) => [
      page.number,
      page.previewPath,
      page.sourcePath ?? null,
      page.sourcePageNumber ?? null,
      page.sourceOccurrenceId ?? null,
    ]),
    document.lineItems.map((row) => [row.rowNumber, rowEvidence(row)]),
  ]);
export type LineDraftContext = {
  document: DocumentResult;
  documentMutationToken: string;
  tableIdentity: string;
  groundedContract: string;
  ambiguousContract: string;
  evidenceCounts: ReadonlyMap<string, number>;
};
/** Ephemeral render work only. Never cache this across authoritative snapshots;
 * save admission calls the helpers without a context and always reads afresh. */
export const createLineDraftContext = (document: DocumentResult): LineDraftContext => {
  const evidenceCounts = new Map<string, number>();
  for (const row of document.lineItems) {
    const evidence = rowEvidence(row);
    evidenceCounts.set(evidence, (evidenceCounts.get(evidence) ?? 0) + 1);
  }
  const documentMutationToken = reviewDocumentMutationToken(document);
  const tableIdentity = lineDraftTableIdentity(document);
  return {
    document,
    documentMutationToken,
    tableIdentity,
    evidenceCounts,
    groundedContract: JSON.stringify([tableIdentity, null]),
    ambiguousContract: JSON.stringify([tableIdentity, documentMutationToken]),
  };
};
export const lineDraftBinding = (
  document: DocumentResult,
  row: LineItemRow,
  cell: LineDraftCell,
  context?: LineDraftContext,
): FieldDraftBinding => {
  const projection = context?.document === document ? context : createLineDraftContext(document);
  const evidence = rowEvidence(row);
  const grounded =
    (row.evidenceLineIndexes?.length ?? 0) > 0 ||
    Object.entries(row.cellEvidence ?? {}).some(
      ([key, value]) => key.endsWith('LineIndexes') && Array.isArray(value) && value.length > 0,
    );
  const unique = grounded && projection.evidenceCounts.get(evidence) === 1;
  const token = projection.documentMutationToken;
  return {
    documentId: document.id,
    fieldId: `line:${row.rowNumber}:${cell}`,
    name: `Line ${row.rowNumber} ${cell === 'unitPrice' ? 'unit price' : cell}`,
    documentMutationToken: token,
    fieldContractToken: unique ? projection.groundedContract : projection.ambiguousContract,
    pageNumber: row.pageNumber,
    baseValue: lineDraftText(row, cell),
  };
};
export const lineDraftMatchesRow = (
  draft: FieldDraft,
  document: DocumentResult,
  context?: LineDraftContext,
) => {
  const target = lineDraftTarget(draft);
  const row = target && document.lineItems.find((row) => row.rowNumber === target.rowNumber);
  return Boolean(
    row &&
    target &&
    draft.documentId === document.id &&
    draft.fieldContractToken ===
      lineDraftBinding(document, row, target.cell, context).fieldContractToken,
  );
};
/**
 * Where typing in one cell goes when the owner's save of another cell lands under it. The saved copy
 * the engine returns differs from the paint the typing began on (it marks the row operatorTyped, for
 * one), so the typing would be refused as "document changed" mid-row, and a row without evidence
 * locked. It moves to the saved copy only when nothing it rests on moved: it is still being typed,
 * it began on a revision that save produced (`producedTokens`: before admission or the paint), the
 * table's layout is the same, and its cell still holds the text the typing began from.
 */
export const lineDraftRebaseBinding = (
  draft: FieldDraft,
  document: DocumentResult,
  producedTokens: ReadonlySet<string>,
): FieldDraftBinding | null => {
  const target = lineDraftTarget(draft);
  if (
    !target ||
    draft.state !== 'editing' ||
    draft.documentId !== document.id ||
    !producedTokens.has(draft.documentMutationToken)
  )
    return null;
  let tableIdentity: unknown;
  try {
    tableIdentity = JSON.parse(draft.fieldContractToken)[0];
  } catch {
    return null;
  }
  if (tableIdentity !== lineDraftTableIdentity(document)) return null;
  const row = document.lineItems.find((item) => item.rowNumber === target.rowNumber);
  if (!row || lineDraftText(row, target.cell) !== draft.baseValue) return null;
  const binding = lineDraftBinding(document, row, target.cell);
  return binding.documentMutationToken === draft.documentMutationToken &&
    binding.fieldContractToken === draft.fieldContractToken
    ? null
    : binding;
};
export const lineDraftSavedOutcome = (
  draft: FieldDraft,
  saved: DocumentResult | undefined,
): FieldDraftOutcome => {
  const target = lineDraftTarget(draft);
  const row =
    saved?.id === draft.documentId && target
      ? saved.lineItems.find((row) => row.rowNumber === target.rowNumber)
      : undefined;
  const expected = target && readLineDraftValue(target.cell, draft.value);
  let sameTable = false;
  try {
    sameTable = Boolean(
      saved && JSON.parse(draft.fieldContractToken)[0] === lineDraftTableIdentity(saved),
    );
  } catch {
    /* Invalid bindings fail closed. */
  }
  // Numeric normalization is intentional ("3.00" -> 3). Acknowledging the raw
  // spelling is safe only after comparing the actual native-returned cell.
  return sameTable &&
    row &&
    target &&
    expected !== undefined &&
    Object.is(row[target.cell] ?? null, expected)
    ? { status: 'saved', value: draft.value }
    : {
        status: 'refused',
        reason: 'Not saved. Your typing is kept; check the current row before trying again.',
      };
};
export type LineDraftEvent =
  | { type: 'line-draft-sync'; draft: FieldDraft }
  | {
      type: 'line-draft-result';
      draft: Extract<FieldDraftEvent, { type: 'field-draft-result' }>['draft'];
      outcome: FieldDraftOutcome;
    }
  | {
      type: 'line-draft-discarded';
      draft: Extract<FieldDraftEvent, { type: 'field-draft-discarded' }>['draft'];
    };
export const lineDraftEvent = (event: FieldDraftEvent): LineDraftEvent => {
  if (event.type === 'field-draft-sync') return { ...event, type: 'line-draft-sync' };
  if (event.type === 'field-draft-result') return { ...event, type: 'line-draft-result' };
  return { ...event, type: 'line-draft-discarded' };
};
